<template>
  <div class="product-gallery">
    <div
      class="product-gallery__slider"
      ref="mainGallery"
    >
      <button
        class="product-gallery__button product-gallery__button--prev"
        ref="prevSlideButton"
      />
      <div
        class="product-gallery__slides"
        data-pswp-uid="1"
      >
        <template
          v-for="(productImage, index) in productImages"
          :key="index"
        >
          <div
            class="product-gallery__slide"
            v-if="productImage.type === 'IMAGE'"
          >
            <image-simple
              v-if="!isMobile"
              class="product-gallery__image"
              :image-src="productImage.big"
              :alt="$t('global.gallery.select.image.alt', [index + 1, productName])"
              @click="imageClickHandler(index)"
              role="button"
              :tabindex="index === currentGalleryIndex ? '0' : '-1'"
              :aria-label="$t('global.gallery.select.image.aria.label', [index + 1, productName])"
              @keydown="handleKeyDown"
            />
            <img
              v-else-if="(isMobile || !isMagnify)"
              class="product-gallery__image"
              :src="productImage.big"
              :title="productName"
              :alt="$t('global.gallery.mobile.image.alt', [index + 1, productName])"
              width="363"
              height="204"
              :loading="index === 0 ? 'eager' : 'lazy'"
              @click="imageClickHandler(index)"
              role="button"
              :tabindex="index === currentGalleryIndex ? '0' : '-1'"
              @keydown="handleKeyDown"
              :aria-label="$t('global.show.image.big.aria.label', [index + 1, productName ])"
            >
          </div>
          <div
            v-else
            class="product-gallery__slide"
          />
        </template>
      </div>
      <button
        href="#"
        class="product-gallery__button product-gallery__button--next"
        ref="nextSlideButton"
      />
    </div>
    <div
      v-if="productImages.length > 1"
      class="product-gallery__pagination"
      ref="pagination"
    />

    <div
      class="product-gallery__thumbnails"
      ref="thumbnails"
    >
      <button
        v-if="productImages.length > 5"
        class="product-gallery__thumb-button product-gallery__thumb-button--prev"
        ref="prevThumbSlideButton"
      />
      <div
        class="product-gallery__thumb-slides"
      >
        <button
          class="product-gallery__thumb-slide product-gallery__thumb-wrapper"
          v-for="(productImage, index) in productImages"
          :key="index"
          :class="{ 'product-gallery__thumb-slide--active': (index === currentGalleryIndex ) }"
          @click="updateCurrentIndex(index)"
          role="button"
          :aria-label="$t('global.change.image.slide.aria.label', [index + 1, productName ])"
        >
          <img
            class="product-gallery__thumbnail"
            :src="productImage.thumbnail"
            :title="productName"
            :alt="$t('global.gallery.thumbnail.alt', [index + 1, productName ])"
            width="101"
            height="76"
            loading="lazy"
          >
          <span
            v-if="productImage.type === 'YOUTUBE_VIDEO'"
            class="product-gallery__thumbnail-play-btn"
            @click="updateCurrentIndex(index)"
            role="button"
            @keydown="handleKeyDown"
            :aria-label="$t('global.change.slide.video.aria.label', [index + 1 ])"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="product-gallery__svg-center"
            >
              <path
                class="product-gallery__inner-play-btn"
                d="M17.5025 11.0464L7.67907 5.15317C7.51267 5.05449 7.32415 5.00174 7.13194 5.00004C6.93973 4.99835 6.75038 5.0478 6.58234 5.14355C6.41432 5.23929 6.27336 5.37807 6.1732 5.54635C6.07306 5.71461 6.01714 5.90665 6.01093 6.10369V17.7001C5.98164 17.9128 6.01158 18.1297 6.09726 18.3259C6.18295 18.5219 6.32091 18.6892 6.49523 18.8084C6.66957 18.9276 6.87317 18.9939 7.08262 18.9996C7.29206 19.0053 7.49882 18.9502 7.67907 18.8407L17.5025 12.9475C17.6554 12.8433 17.7809 12.702 17.8677 12.5363C17.9546 12.3704 18 12.1851 18 11.9969C18 11.8087 17.9546 11.6234 17.8677 11.4575C17.7809 11.2917 17.6554 11.1504 17.5025 11.0464Z"
              />
            </svg>
          </span>
        </button>
      </div>
      <button
        v-if="productImages.length > 5"
        class="product-gallery__thumb-button product-gallery__thumb-button--next"
        ref="nextThumbSlideButton"
      />
    </div>
  </div>
</template>

<script>
import {A11y, Navigation, Pagination, Swiper} from 'swiper'
import {breakpoints} from '../../variables/variables.js'
import ImageSimple from '../imageSimple/imageSimple.vue'
import Modal from '../modal/modal.vue'
import swiperMixin from '../../utilities/vueMixins/swiperMixin/swiperMixin.js'
import YoutubePlayer from '../../utilities/js/youtubePlayer/youtube'
import pigeon from '../../utilities/js/pigeon/pigeon'

export default {
  components: {
    ImageSimple,
    Modal
  },
  mixins: [
    swiperMixin
  ],
  props: {
    name: String,
    productName: String
  },
  data() {
    return {
      modalId: 'videoModal',
      isMagnify: true,
      isMobile: window.innerWidth < breakpoints.laptop,
      yt: new YoutubePlayer(),
      photoSwipe: false,
      thumbnailsCarousel: Object,
      mainCarousel: Object
    }
  },
  computed: {
    productImages() {
      return this.$store.state.productPage.productData.productDetails.images
    },
    currentGalleryIndex() {
      return this.$store.state.productPage.productData.currentGalleryIndex
    }
  },
  mounted() {
    window.addEventListener('breakpointChange', this.breakpointChangeHandler.bind(this))

    this.createCarousel()

    //update Swipers on variationChange
    pigeon.subscribe('productGallery:resetSwiper', () => {
      this.thumbnailsCarousel.destroy(true, true)
      this.mainCarousel.destroy(true, true)
      this.$nextTick(() => {
        this.createCarousel()
      })
    })

    // pigeon.subscribe('modal:updated', (data) => {
    //   if (data.visible && data.id === this.modalId) {
    //     VideoPlayer.Collection.addPlayerById("ads-video-player");
    //   }
    // });
    // if (this.productVideos) {
    //   this.loadVideoLibrary();
    // }
    if (this.isMobile) {
      this.isMagnify = false
    }
  },
  updated() {
    this.$nextTick(() => {
      this.mainCarousel.update(true)
    })
  },
  methods: {
    createCarousel() {
      let product = this

      Swiper.use([Navigation, Pagination, A11y])

      this.thumbnailsCarousel = new Swiper(this.$refs.thumbnails, {
        slidesPerView: 5,
        slidesPerGroup: 1,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        wrapperClass: 'product-gallery__thumb-slides',
        slideClass: 'product-gallery__thumb-slide',
        slideVisibleClass: 'product-gallery__thumb-slide--visible',
        slidePrevClass: 'product-gallery__thumb-slide--prev',
        slideNextClass: 'product-gallery__thumb-slide--next',
        slideThumbActiveClass: 'product-gallery__thumb-slide--active',
        spaceBetween: 10,
        simulateTouch: this.isSimulateTouch,
        navigation: {
          prevEl: this.productImages.length > 5 ? this.$refs.prevThumbSlideButton : '',
          nextEl: this.productImages.length > 5 ? this.$refs.nextThumbSlideButton : '',
          hiddenClass: 'product-gallery__thumb-button--hidden',
          disabledClass: 'product-gallery__thumb-button--disabled'
        }
      })

      this.mainCarousel = new Swiper(this.$refs.mainGallery, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        wrapperClass: 'product-gallery__slides',
        slideClass: 'product-gallery__slide',
        slideActiveClass: 'product-gallery__slide--active',
        slidePrevClass: 'product-gallery__slide--prev',
        slideNextClass: 'product-gallery__slide--next',
        spaceBetween: 0,
        simulateTouch: this.isSimulateTouch,
        navigation: {
          prevEl: this.$refs.prevSlideButton,
          nextEl: this.$refs.nextSlideButton,
          hiddenClass: 'product-gallery__button--hidden',
          disabledClass: 'product-gallery__button--disabled'
        },
        pagination: {
          el: this.$refs.pagination,
          clickable: true,
          bulletClass: 'product-gallery__pagination-bullet',
          bulletActiveClass: 'product-gallery__pagination-bullet--active'
        },
        thumbs: {
          swiper: this.thumbnailsCarousel
        },
        on: {
          slideChange: function () {
            product.updateStoreIndex(this.realIndex)
            product.yt.reset()
            if (product.productImages[this.realIndex].type === 'YOUTUBE_VIDEO') {
              product.$nextTick(() => {
                product.handlePlayClick(product.productImages[this.realIndex])
              })
            }
          }
        }
      })
    },
    imageClickHandler(index) {
      this.photoSwipe = true
      this.$store.dispatch('productPage/imageGalleryClicked', {
        showImage: index
      })
    },
    // loadVideoLibrary() {
    //   let videoLibraryScript = document.createElement('script');
    //   videoLibraryScript.setAttribute('src', this.productVideos.scriptPath);
    //   document.head.appendChild(videoLibraryScript)
    // },
    breakpointChangeHandler() {
      this.isMobile = window.innerWidth < breakpoints.laptop

      this.isMagnify = !this.isMobile
    },
    updateStoreIndex(index) {
      this.$store.commit('productPage/indexUpdate', index)
    },
    updateCurrentIndex(index) {
      this.updateStoreIndex(index)
      this.mainCarousel.slideTo(index, false)
    },
    handlePlayClick(video) {
      const data = {
        youtubeVideoId: video.videoId,
        playerId: 'youtube-player',
        width: 682,
        height: 204
      }
      this.yt.handlePlay(data, true)
    },
    handleKeyDown(event) {
      if (event.key === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        event.target.click();
      }
    }
  },
  watch: {
    currentGalleryIndex() {
      this.mainCarousel.slideTo(this.currentGalleryIndex, false)
    }
  }
}

</script>
